import { Injectable, Injector, inject } from '@angular/core';
import { ZeichnungStoreService, DB_CONNECTION_CONFIGS, OfflineModeService, PROJECT_PROVIDER } from '@luzula/luzula-lib';
import { DatabaseConfigService } from './database-config.service';
import { ProjectService } from './project.service';

/**
 * bpweb-Konfiguration des Zeichnungen-Stores (dedizierte DB `bpweb-zeichnungen`,
 * ein Doc je Feature). Lifecycle/CRUD/Sync kommen aus {@link ZeichnungStoreService};
 * hier nur Remote-URL (inkl. Credentials) und Name der lokalen Replik.
 */
@Injectable({ providedIn: 'root' })
export class ZeichnungenService extends ZeichnungStoreService {
	private dbConfig = inject(DatabaseConfigService);
	private dbConnectionConfigs = inject(DB_CONNECTION_CONFIGS);
	private injector = inject(Injector);

	/**
	 * Gruppen des Projekts (feature/user-groups): neue/geaenderte Zeichnungs-Docs erben additiv die
	 * groups des Projekts. Lazy ueber PROJECT_PROVIDER (Injektions-Kreis vermeiden). Nur wenn das
	 * aktuelle Projekt-Doc zur projectId passt (sonst keine Stempelung).
	 */
	protected override groupsForProject(projectId: string): string[] | undefined {
		const ps = this.injector.get(PROJECT_PROVIDER, null) as { currentProjectDoc?: () => { _id?: string; groups?: string[] } | null } | null;
		const doc = ps?.currentProjectDoc?.();
		return doc && doc._id === projectId ? doc.groups : undefined;
	}

	/**
	 * Das Zeichenrecht, erst im Moment der Frage geholt.
	 *
	 * **Nicht `inject(ProjectService)` als Feld** (Befund 11.09.2026): Der
	 * `ProjectService` holt sich beim Bauen seinerseits diesen Dienst. Zwei
	 * Dienste, die beim Bauen aufeinander warten, sind ein Ring — Angular bricht
	 * dann den ganzen Start ab (`NG0200`), die App bleibt im Splash stehen. Beim
	 * Klären gefragt wird das Recht ohnehin erst lange nach dem Start.
	 * Festgehalten in `kein-ring-unter-diensten.spec.ts`.
	 */
	private get ps(): ProjectService {
		return this.injector.get(ProjectService);
	}

	/**
	 * Zwei Fassungen einer Zeichnung darf nur klären, wer auch sonst zeichnen
	 * dürfte — `canEditDrawings()`, also Bearbeiter-Member oder Entwickler, und
	 * nur solange das Projekt nicht abgeschlossen ist.
	 *
	 * **Warum das fehlte** (Nutzer-Befund 11.09.2026): Die Klärung schrieb
	 * unmittelbar auf die lokale Datenbank, an jeder Prüfung vorbei. Ein
	 * Kontrolleur oder Datennutzer hätte darüber eine Zeichnung überschreiben
	 * können, obwohl er gar nicht zeichnen darf; in einem abgeschlossenen
	 * Projekt hätte es ebenfalls funktioniert.
	 */
	override darfAendern(_doc: any): boolean {
		return this.ps.canEditDrawings();
	}

	/**
	 * Merkt am Projekt, dass gerade jemand gezeichnet hat.
	 *
	 * Treibt die Spalte „Letzte Änderung" in der Projektauswahl; das Drosseln
	 * und das Schreiben stehen im Projekt-Dienst der Bibliothek.
	 */
	protected override merkeArbeit(projectId: string): void {
		this.ps.merkeArbeitImProjekt(projectId);
	}

	/**
	 * Offline gestellt? Dann schiebt der Speicher nichts von selbst zum Server;
	 * alles wartet auf den Abgleich (Nutzer-Befund 25.09.2026: eine Linie ging
	 * am Schreibtisch trotz Offline-Modus sofort hoch, die Aufstellung vor dem
	 * Übertragen fand danach nichts mehr).
	 *
	 * Erst im Moment der Frage geholt, nicht beim Bauen — wie beim Zeichenrecht.
	 */
	protected override imOfflineModus(): boolean {
		return this.injector.get(OfflineModeService).userOfflineMode();
	}

	/** Remote-Master = `bpweb-zeichnungen` (nur online). */
	protected get remoteDbUrl(): string | null {
		if (!navigator.onLine) return null;
		const conn = this.dbConnectionConfigs[0];
		return conn ? conn.url(this.dbConfig.zeichnungenDbName) : null;
	}

	/** Lokale Replik (konstanter Name). */
	protected get localCacheDbName(): string {
		return `${this.dbConfig.zeichnungenDbName}-cache`;
	}

	/** Gateway-Auth: Bearer-Header pro Request für die Remote-Replikation. */
	protected override authHeaders(): Record<string, string> | null | undefined {
		return this.dbConnectionConfigs[0]?.getAuthHeaders?.() ?? null;
	}

	/**
	 * Abgelaufener Token: einmal still erneuern, dann wiederholt der fetch-Wrapper
	 * die Anfrage. Ohne das blieb die Replikation nach Token-Ablauf bei 401 stehen,
	 * bis die Seite neu geladen wurde.
	 */
	protected override authRefresh(): Promise<boolean> {
		return this.dbConnectionConfigs[0]?.refreshAuth?.() ?? Promise.resolve(false);
	}

	/**
	 * Auf dem Nur-Lese-Weg festgefahren: zurueck auf die richtige Adresse.
	 *
	 * Die Verbindung waehlt ihren Weg nur beim Aufbau. Startet die App ohne
	 * greifbaren Token (Standby, wackliges Netz), bleibt sie auf
	 * `/db/public/...` stehen — Ansehen geht, Speichern bekommt ein 405, und
	 * das fuer die ganze Sitzung (Nutzer-Befund 01.09.2026).
	 */
	protected override adressReparatur(url: string): string | undefined {
		return this.dbConnectionConfigs[0]?.repariereAdresse?.(url);
	}
}
